import { fetchV2, v2 } from "../../lib/api-v2";
import type {
  Alert,
  DictationResult,
  CallAudioGrant,
  CallDetailResponse,
  GuidanceOutcome,
  HandbackOutcome,
  RosterResponse,
  RuleSettingsResponse,
  SupervisionRuleConfiguration,
  Shift,
  ShiftRota,
} from "./types";

/**
 * Supervision REST calls.
 *
 * Uses the shared v2 client, which prefixes /api/v2, injects auth, account and
 * request-id headers, and normalises failures into ApiV2Error.
 *
 * The live stream does NOT go through here: fetchV2 resolves a parsed JSON body,
 * which a long-lived SSE response never produces. See use-supervision-stream.ts.
 */

/** Roster snapshot — bootstraps the console and backs the polling fallback. */
export const fetchSupervisedCalls = () => v2.get<RosterResponse>("/supervision/calls");

export const fetchRuleSettings = () =>
  v2.get<RuleSettingsResponse>("/supervision/rule-settings");

export const saveRuleSettingsDraft = (
  expectedVersion: number,
  settings: SupervisionRuleConfiguration,
) => v2.put<RuleSettingsResponse>("/supervision/rule-settings/draft", {
  expected_version: expectedVersion,
  settings,
});

export const validateRuleSettings = (settings: SupervisionRuleConfiguration) =>
  v2.post<{ valid: true; settings: SupervisionRuleConfiguration }>(
    "/supervision/rule-settings/validate",
    { settings },
  );

export const publishRuleSettings = (expectedVersion: number) =>
  v2.post<RuleSettingsResponse>("/supervision/rule-settings/publish", {
    expected_version: expectedVersion,
  });

/** Send an instruction the AI applies on its next reply. */
export const sendGuidance = (
  callId: string,
  body: { text: string; urgency?: "next_turn" | "now"; source?: "typed" | "suggested" },
) =>
  v2.post<GuidanceOutcome>(`/supervision/calls/${encodeURIComponent(callId)}/guide`, {
    urgency: "next_turn",
    ...body,
  });

/**
 * One call in full: the transcript window, every alert, and the customer's number.
 *
 * The roster deliberately carries none of that. Fetching it per focused call keeps
 * twenty-five tiles' worth of transcript and PII off the wall entirely.
 */
export const fetchCallDetail = (callId: string) =>
  v2.get<CallDetailResponse>(`/supervision/calls/${encodeURIComponent(callId)}`);

const alertPath = (alertId: string, action: string) =>
  `/supervision/alerts/${encodeURIComponent(alertId)}/${action}`;

/** Mark an alert seen, so it stops competing with the ones nobody has looked at. */
export const acknowledgeAlert = (alertId: string) =>
  v2.post<{ alert: Alert }>(alertPath(alertId, "acknowledge"));

/**
 * Record that a rule fired wrongly here.
 *
 * Terminal for the life of the call, and the only measurement of whether the detection
 * thresholds are set right -- without it, alert fatigue is invisible until people have
 * already stopped trusting the colours.
 */
export const markAlertFalsePositive = (alertId: string, note = "") =>
  v2.post<{ alert: Alert }>(alertPath(alertId, "false-positive"), { note });

const callPath = (callId: string, action: string) =>
  `/supervision/calls/${encodeURIComponent(callId)}/${action}`;

/** Join a live call read-only. Many supervisors may listen; none can be heard. */
export const startListening = (callId: string) =>
  v2.post<CallAudioGrant>(callPath(callId, "listen"));

/**
 * Take the conversation over: the AI speaks `stallText`, then goes quiet.
 *
 * Rejects with 409 when someone else holds the call, or when the runtime refused the
 * hold — in both cases no publish token is issued, because a second voice on a live
 * customer call is worse than a failed takeover.
 */
export const startTakeover = (
  callId: string,
  body: { stall_text?: string; hold_seconds?: number } = {},
) => v2.post<CallAudioGrant>(callPath(callId, "takeover"), body);

/** Say "still here". Stop calling this and the AI hands itself back. */
export const sendHeartbeat = (callId: string) => v2.post(callPath(callId, "heartbeat"));

/** Give the call back to the AI, briefed on what it missed. */
export const handBack = (callId: string, briefing: string) =>
  v2.post<HandbackOutcome>(callPath(callId, "handback"), { briefing });

/** End a live call, through the agent so recording and post-call still happen. */
export const endCall = (callId: string, reason = "") =>
  v2.post<GuidanceOutcome>(callPath(callId, "end"), { reason });

/**
 * Turn a recording into editable draft text.
 *
 * Deliberately not call-scoped and deliberately inert: it returns text and does nothing
 * else. The operator edits it and presses Send themselves, so a misheard word never
 * reaches a customer on its own.
 */
export const dictate = (audio: Blob, language = "") => {
  const form = new FormData();
  // The filename is required for the server to see this as a file part; the extension is
  // cosmetic, since the content type is what Deepgram reads.
  form.append("audio", audio, "dictation.webm");
  if (language) form.append("language", language);
  return fetchV2<DictationResult>("/supervision/dictate", { method: "POST", body: form });
};

/** Who is on the floor, and the handover waiting for whoever joins it. */
export const fetchShiftRota = () => v2.get<ShiftRota>("/supervision/shift");

/**
 * Go on shift.
 *
 * Explicit rather than implied by opening the page: a tab left open in a background window
 * used to count as an available supervisor, which made splitting work across people
 * impossible to do honestly.
 */
export const startShift = (comfortableLoad: number) =>
  v2.post<{ shift: Shift; on_shift: Shift[] }>("/supervision/shift/start", {
    comfortable_load: comfortableLoad,
  });

/** Come off shift, releasing your share of the roster to whoever is still on. */
export const endShift = () => v2.post<{ on_shift: Shift[] }>("/supervision/shift/end");


import { getManualFlag, removeManualFlag, saveManualFlag } from "./lib/flags-store";

export const fetchFlags = (cursor?: string) =>
  v2.get<import("./types").FlagsResponse>(`/supervision/flags${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`);
export const flagCall = async (id: string, note = "") => {
  try {
    const res = await v2.put<{flag: import("./types").ReviewFlag}>(callPath(id, "flag"), {note});
    if (res?.flag) {
      saveManualFlag(id, note, res.flag.call);
    }
    return res;
  } catch {
    const flag = saveManualFlag(id, note);
    return { flag };
  }
};
export const editFlag = async (id: string, note: string) => {
  try {
    const res = await v2.patch<{flag: import("./types").ReviewFlag}>(callPath(id, "flag"), {note});
    if (res?.flag) {
      saveManualFlag(id, note, res.flag.call);
    }
    return res;
  } catch {
    const flag = saveManualFlag(id, note);
    return { flag };
  }
};
export const unflagCall = async (id: string) => {
  removeManualFlag(id);
  try {
    return await v2.delete(callPath(id, "flag"));
  } catch {
    return {};
  }
};
